import { IonContent, isPlatform } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInput } from '@/components/FormInput';
import { InfoProtection } from '@/components/InfoProtection';
import { UiTypography } from '@/components/ui/Typography';
import { useAccount } from '@/hooks/useAccount';
import { useCreateStripeClient } from '@/hooks/useCreateStripeClient';
import { amplitudeModule } from '@/modules/amplitude';
import { ApiBotType, apiV1GetMeAuthRequest } from '@/modules/api';
import { getBotSlug } from '@/modules/botSlug';
import { chatPinner } from '@/modules/chatPinner';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { PhotosTile } from '@/pages/Onboarding/components/PhotoTile';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { useLayoutSelections } from '@/pages/Onboarding/hooks/useLayoutSelections';
import { useQueryPersonalizedAis } from '@/pages/Onboarding/hooks/useQueryPersonalizedAis';
import { checkIsValidateEmail } from '@/pages/Onboarding/utils/checkIsValidEmail';
import { sliceTwins } from '@/pages/Onboarding/utils/sliceTwins';
import { userService } from '@/services/user';
import { trackOnboardingComplete } from '@/utils/analytics';
import { canLogEvents } from '@/utils/canLogEvents';
import { redirectToWebapp } from '@/utils/redirectToWebapp';

import { useOwnAiPreloadImages } from '../../hooks/useOwnAiPreloadImages';
import { layoutQueryParams } from '../../params';
import { LayoutDataType } from '../../types';

import { EmailStyles } from './Email.styles';

export const ScreenEmail: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Email',
      });
    }

    layoutQueryParams.removeStepsData();
  }, []);

  const { refreshUser, user } = useAccount();
  const { ageGeneration, bodyShape, hairColor } = useLayoutSelections();
  useOwnAiPreloadImages();

  const form = useFormContext<LayoutDataType>();

  const inputRef = React.useRef<HTMLIonInputElement>(null);

  const formCharacters = form.watch('characters');
  const formEmail = form.watch('email');

  const isValidEmail = checkIsValidateEmail(formEmail);

  const { createStripeClient, isCreatingStripeClient } =
    useCreateStripeClient();

  React.useEffect(() => {
    if (isPlatform('android') || isPlatform('desktop')) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      });
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      }, 1000);
    }
  }, []);

  const [twinsData, setTwinsData] = React.useState<ApiBotType[]>(
    formCharacters || [],
  );
  const sicedTwins = sliceTwins(twinsData || []);

  useQueryPersonalizedAis({
    enabled: !twinsData.length || !formCharacters.length,
    form,
    onSuccess: (data) => {
      setTwinsData(data.twins);
    },
  });

  const handleNextStep = async (): Promise<void> => {
    if (!formEmail || !isValidEmail) return;

    try {
      const lowerCaseEmail = formEmail.toLowerCase().trim();

      trackOnboardingComplete({ email: lowerCaseEmail });

      await Promise.all([
        createStripeClient(lowerCaseEmail),
        userService.createOrUpdate({
          onboarding_data: {
            onboarded: true,
          },
        }),
      ]);

      const slug = getBotSlug({
        ageGeneration,
        hairColor,
        bodyShape,
        gender: 'female',
      });
      if (user && slug) {
        chatPinner.pin(slug, user.id);
      }

      if (webCampaign.setup.redirectToWebappAfter === 'email') {
        const auth = await apiV1GetMeAuthRequest();

        if (auth?.token) {
          redirectToWebapp(auth.token);
          return;
        }
      }

      refreshUser();
    } catch (error) {
      console.error('Can not create a stripe user', error);
    }
  };
  return (
    <EmailStyles>
      <OnboardingNavigation />

      <IonContent>
        <OnboardingTitle className="onboarding-email-title">
          We’ve added{' '}
          <UiTypography variant="inherit" color="danger" component="span">
            1578
          </UiTypography>{' '}
          AI characters
          <br />
          customized to your preferences
        </OnboardingTitle>

        <PhotosTile slicedAis={sicedTwins} gender="female" />

        <OnboardingTitle className="email-title">
          Enter your email to create a personal account
        </OnboardingTitle>

        <div className="email-input-container">
          <FormInput
            form={form}
            hidden={false}
            onSubmit={handleNextStep}
            disableButton={!isValidEmail}
            ref={inputRef}
            name="email"
            type="email"
            placeholder="Type your email"
            loading={isCreatingStripeClient}
          />
        </div>
        <div className="email-protection">
          <InfoProtection />
        </div>
      </IonContent>
    </EmailStyles>
  );
};
